import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import has from 'lodash/has';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import set from 'lodash/set';
import findLastIndex from 'lodash/findLastIndex';
import isNil from 'lodash/isNil';
import uniqBy from 'lodash/uniqBy';
import isEmpty from 'lodash/isEmpty';
import * as log from 'loglevel';
import {
  Events,
  replaceSuggestionEntityTags,
  appendSenderProfile,
} from '@/libs';

import {
  DEFAULT_KB_MODE,
  isConversationsPage,
  isStateScopeMine,
  isStateChannelAll,
  KB_MODES,
  MSG_SUGGESTION_MEMORY_LIMIT,
} from '@/constants';


function isUnread(item) {
  return item.unread_message_count > 0;
}

// TODO: Move over to getters. It does not need to be in mutations
function updateConversationIndicator(state, list) {
  /**
   * You are in messages tab and not filtering and
   * in scope Mine and channel All => not showing indicator
   */
  const isInCorrectScope = isConversationsPage() &&
    isStateScopeMine(state) &&
    isStateChannelAll(state);
  if (isInCorrectScope && isNil(state.filter)) {
    return false;
  }

  /** Since we are outside of scope Mine and channel All
   * we need to look up in list if we have unread message.
   */
  const item = list.find(e => isUnread(e));
  if (item) {
    // we found unread message and we are outside of messages page or outside of mine scope
    if (!isConversationsPage() || !isStateScopeMine(state)) {
      return true;
    }

    /** We found unread message and are in scope mine and filtering,
     * we want to show indicator if unread message is not in filtered list
     */
    if (isStateScopeMine(state) && !isNil(state.filter)) {
      return list.filter(c => state.conversationsByConversationId[c.id] && isUnread(c)).length === 0;
    }

    /** We found unread message and are inside mine scope not filtering,
     * but message is from other channel than current channel
     */
    if (item.channel !== state.channel) {
      return true;
    }
    /**
     * We also want to check if there are more unread messages and if they are are from other
     * channels than in current channel so we need notify user with indicator
     */
    return list.findIndex(e => ((isUnread(e)) && (e.channel !== state.channel))) >= 0;
  }
  return false;
}

function updateSuggestionEntities({ conversation, customer, agents, suggestions }) {
  conversation.suggestions = replaceSuggestionEntityTags({
    suggestions,
    customer,
    primaryAgent: conversation.primaryAgent,
    lockedAgent: conversation.locked_by,
    otherConversationAgents: agents,
  });
}

export default {
  SET_ATTACHMENTS_HISTORY(state, attachmentsHistory) {
    state.attachmentsHistory = attachmentsHistory;
  },

  ADD_MESSAGE(state, message) {
    // Strong consistency validation and throw an exception
    // The below conditions are added to catch any inconsistency
    if (isNil(state.selected) || isNil(state.selected.id) || isNil(state.selected.customer_id) || isNil(message.customer_id)) {
      throw new Error(`Inconsistent state:
        Unexpected state ${get(state, 'selected')}, ${get(state, 'selected.id')},
        ${get(state, 'selected.customer_id')}, ${get(message, 'customer_id')}`);
    }

    // check with messages array
    if (!isEmpty(state.messages) && state.messages[0].customer_id !== message.customer_id) {
      throw new Error(`Inconsistent state(state.messages):
        Trying to add message to customer ${state.messages[0].customer_id}
        while selected customer is ${message.customer_id}`);
    }

    // check with selected conversation
    if (state.selected.customer_id !== message.customer_id) {
      throw new Error(`Inconsistent state(state.selected):
        Trying to add message to customer ${message.customer_id}
        while selected customer is ${state.selected.customer_id}`);
    }

    if (get(message, 'payload.event.action') === 'messages.mark.read') {
      return;
    }

    appendSenderProfile(message, this.state.agents);

    const messages = state.messages;
    const index = messages.findIndex(e => e.id === message.id);
    if (get(message, 'payload.message_type') === 'reply') {
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        if (messages[i].id === message.payload.reply.postback.reply_to) {
          messages[i]['replyitem'] = message.payload.reply;
          break;
        }
      }
    } else if (index !== -1) {
      messages[index] = message;
    } else {
      messages.push(message);
    }
  },

  REPLACE_MESSAGE(state, newMessage) {
    appendSenderProfile(newMessage, this.state.agents.list);

    // current customer's message is in either
    // state.selected or state.pastConversations
    let messages = get(state, 'messages', []);
    let foundIndex = messages.findIndex(m => m.id === newMessage.id);
    if (foundIndex !== -1) {
      messages[foundIndex] = newMessage;
    } else {
      for (const conv of state.pastConversations) {
        messages = get(conv, 'messages', []);
        foundIndex = messages.findIndex(m => m.id === newMessage.id);
        if (foundIndex !== -1) {
          messages[foundIndex] = newMessage;
          break;
        }
      }
    }
  },

  // TODO:: Remove since DEPRECATED
  // To be added to the history where there is already an active selected conversation
  ADD_CONVERSATIONS(state, params) {
    prepareForState(params);
    state.pastConversations.unshift(...params.conversations);
    const existingIds = new Set(state.messages.map(message => message.id));
    const newMessages = params.messages.filter(message => !existingIds.has(message.id));
    state.messages.unshift(...newMessages);
  },

  ADD_CONVERSATIONS_AND_MESSAGES(state, params) {
    prepareForState(params);
    const existingIds = new Set(state.messages.map(message => message.id));
    const newMessages = params.messages.filter(message => !existingIds.has(message.id));
    if (params.direction === 'next') {
      state.pastConversations.unshift(...params.conversations);
      state.messages.unshift(...newMessages);
    } else {
      state.pastConversations = state.pastConversations.concat(...params.conversations);
      state.messages = state.messages.concat(...newMessages);
    }
  },

  CUSTOMER_CONVERSATIONS_LOADING(state, value) {
    state.customerConversationsLoading = value;
  },

  TOTAL_CUSTOMER_MESSAGES(state, total) {
    state.totalMessagesOfCustomer = total;
  },

  TYPING_STATUS(state, [value]) {
    state.typing = value;
  },

  CLEAR_LIST(state) {
    state.conversationsByConversationId = {};
    state.pagination = {};
    state.loaded = false;
  },

  CREATE_CONVERSATION(state, conversation) {
    state.conversationsByConversationId[conversation.id] = conversation;
  },

  SET_SYSTEM_MESSAGES_DISPLAY(state, display) {
    state.systemMessagesDisplay = display;
    localStorage.setItem('systemMessagesDisplay', state.systemMessagesDisplay ? 'display' : 'hide');
  },

  UNFOCUS(state) {
    state.focused = null;
  },

  CLEAR_SELECTED_CONVERSATION(state) {
    state.selected = {};
    state.selectedCustomer = null;
    state.messages = [];
    state.pastConversations = [];
  },

  SET_FOCUS(state, customer) {
    state.focused = { customer, customer_id: customer.id };
  },

  SET_PAGINATION(state, pagination) {
    state.pagination = !isNil(state.filter) ? pagination : {};
  },

  UPDATE_CONVERSATION_LOOKUP(state, [list, profile, updated = true, polling = false, activeSort = '', scope = 'all', channel = '']) { // eslint-disable-line
  },

  LOAD_LIST(state, [list, profile, updated = true, polling = false, activeSort = '', scope = 'all', channel = '']) { // eslint-disable-line
    state.activeSort = activeSort;
    // There is a bug here where scope is 'all' even in the 'mine' tab.
    if (scope === 'mine' && channel === 'all') {
      state.newConversationIndicator = updateConversationIndicator(state, list);
    }

    log.debug('LOAD_LIST', list[0], state.scope, scope, polling, state.filter, updated);
    /**
     * We need to update list because we are either in mine and any
     * channel(we always pull for mine/all) or in current state scope and current state channel.
     */
    if (isStateScopeMine(state) || (state.scope === scope && state.channel === channel)) {
      const newLookupByConversation = list.reduce((obj, item) => {
        obj[item.id] = item;
        return obj;
      }, {});
      // None polling request always updates the list
      // if updated is false, it is scrolling so we append to existing list
      if (!polling) {
        if (updated) {
          state.conversationsByConversationId = newLookupByConversation;
        } else {
          state.conversationsByConversationId = {
            ...state.conversationsByConversationId,
            ...newLookupByConversation,
          };
        }

        return;
      }

      // Polling request only updates the list if there is no filter
      if (polling && isNil(state.filter)) {
        state.conversationsByConversationId = newLookupByConversation;
      }
    }
  },

  RESET_KB(state) {
    state.KBMode = cloneDeep(DEFAULT_KB_MODE);
  },

  SELECT_SUGGESTIONS(state, { type, msgId }) {
    const info = state.suggestedItemHistory[type];
    const suggestions = info.suggestionsByMsgId[msgId];
    state.KBMode = {
      name: KB_MODES.SELECTED,
      type,
      items: suggestions,
    };
  },

  // TODO (Gabe) - Remove once suggest item mutation below is used
  SET_DOCUMENTS(state, [conversation, documents]) {
    if (state.selected.id === conversation) {
      state.selected.documents = documents;
    }
  },

  SET_KB_MODE(state, payload) {
    state.KBMode = {
      ...state.KBMode,
      ...payload,
    };
  },

  SET_SUGGEST_ITEM_HISTORY_BY_MESSAGE(state, [suggestionsByMsg, type]) {
    const history = state.suggestedItemHistory[type];
    const { msgIds, suggestionsByMsgId } = history;

    suggestionsByMsg.forEach(kbSuggestion => {
      msgIds.push(kbSuggestion.message_id);
      suggestionsByMsgId[kbSuggestion.message_id] = kbSuggestion[type];
    });
  },

  SET_SUGGEST_ITEM_HISTORY(state, [, rawItems, type]) {
    if (rawItems.length === 0) { return }
    const items = rawItems;
    state.selected[type] = items;

    /**
     * TODO (Gabe) - switch once event conversation returns correct message
     * const lastMsg = conversation.messages[0];
     */
    let lastMsg;
    for (let i = 0; i < state.messages.length; i += 1) {
      const msg = state.messages[i];
      if (msg.sender_type === 'customers') {
        lastMsg = msg;
      }
    }

    if (!lastMsg) return;
    // We only want to keep the last X messages that have suggestions

    const history = state.suggestedItemHistory[type];
    const { msgIds, suggestionsByMsgId } = history;
    // We only want to keep the last X messages that have suggestions
    if (msgIds.length < MSG_SUGGESTION_MEMORY_LIMIT - 1) {
      msgIds.push(lastMsg.id);
      suggestionsByMsgId[lastMsg.id] = items;
    } else {
      // remove oldest item from beginning of array
      const oldestId = msgIds.shift().id;
      delete suggestionsByMsgId[oldestId];
    }
  },

  SET(state, [key, value]) {
    state[key] = value;
  },

  SET_SCOPE(state, scope) {
    state.scope = scope;
  },

  SET_SELECTED_TAB(state, selectedTab) {
    state['selectedTab'] = selectedTab;
  },

  SET_CHANNEL(state, channel) {
    state.channel = channel;
  },

  SET_FILTER(state, filter) {
    if (filter === undefined) {
      state.filter = null;
      return;
    }
    state.filter = filter;
  },

  UPDATE_ALL_COUNTERS(state, counters) {
    state.counters = { ...state.counters, ...counters };
  },

  UPDATE_SPECIAL_COUNTER(state, [name, value]) {
    set(state.counters, name, value);
  },

  SELECT_CONVERSATION(state,
    [{ conversations = [], messages = [], customer }, profile, agentsById, starred = false]) { // eslint-disable-line
    let shouldTagCustomerRead = false;
    state.customerReadMsg = null;

    // Attach read status to the last agent message
    for (const message of messages) {
      if (get(message, 'payload.event.action') === 'messages.mark.read'
        && get(message, 'sender_type') === 'customers' && !shouldTagCustomerRead) {
        shouldTagCustomerRead = true;
      }

      if (get(message, 'sender_type') === 'agents'
        && get(message, 'payload.message_type') !== 'event'
        && shouldTagCustomerRead
        && !state.customerReadMsg) {
        state.customerReadMsg = message; // save it to remove in the future
        state.customerReadMsg['status'] = { state: 'viewed', message: 'Read' };
        break;
      }
    }

    // Chat Panel star indicator to only apply to the last conversation
    const lastConversation = conversations[0];
    lastConversation.starred = starred;

    /** TODO (Gabe) - Remove check once customer is always provided
     *  Check only needed for when selected from direct url
     */

    if (customer) {
      state.selectedCustomer = customer;
      prepareForState({ conversations, messages, agentsById, customer });
    }

    state.selected = conversations.pop();
    state.pastConversations = conversations;
    // Clone messages to avoid shared reference issues
    state.messages = [...messages];
  },

  SET_NOTES(state, [notes, update = true]) {
    if (update) {
      state.notes = notes;
    } else {
      state.notes.push(...notes);
    }
  },

  SET_NOTES_PAGINATION(state, pagination) {
    state.notesPagination = pagination;
  },

  SET_READY_AGENT_NOTES(state, readyAgentNotes) {
    state.readyAgentNotes = readyAgentNotes;
  },

  RESET_READY_AGENT_NOTES(state) {
    state.readyAgentNotes = [];
  },


  SET_SCHEDULES(state, [schedules, update = true]) {
    if (update) {
      state.schedules = schedules;
    } else {
      state.schedules.push(...schedules);
    }
  },

  UPDATE_SELECTED_CONVERSATION(state, [conversation]) {
    if (state.selected.id === conversation.id) {
      state.selected = conversation;
    }
  },

  ADD_NOTE(state, [conversation, note]) {
    if (state.selected.id === conversation) {
      state.notes.unshift(note);
    }
  },

  REMOVE_NOTE(state, [conversation, note]) {
    if (state.selected.id !== conversation) {
      return;
    }

    state.notes = state.notes.filter(e => e.id !== note);
  },

  STAR_CONVERSATION(state, [id, value]) {
    if (state.selected.id === id) {
      set(state.selected, 'isStarred', value);
    }

    const conversation = state.conversationsByConversationId[id];
    if (conversation) {
      conversation.starred = value;
    }
  },

  MARK_AS_LOADED(state, loaded) {
    state.loaded = loaded;
  },

  UPDATE_TEAMS(state, [conversation, teams]) {
    if (state.selected.id === conversation) {
      state.selected.teams = teams;
    }
  },

  UPDATE_TAGS(state, [conversation, tags]) {
    if (state.selected.id === conversation) {
      state.selected.tags = tags;
    }
  },

  UPDATE_CATEGORIES(state, [conversation, categories]) {
    if (state.selected.id === conversation) {
      state.selected.categories = categories;
    }
  },

  // todo - change param names to conversationId
  UPDATE_AGENTS(state, [conversationId, agents, customerId]) {
    if (!state.conversationAgents) {
      state.conversationAgents = {};
    }

    if (!state.conversationAgents[conversationId]) {
      state.conversationAgents[conversationId] = [];
    }

    state.conversationAgents[conversationId] = agents;
    // Only update the agent list if this message applies to the
    // a conversation held in the currently selected customer.
    if (get(state, 'selected.customer_id') === customerId) {
      // TODO (Gabe) - consolidate with conversationAgents.
      state.selected.agents = agents;
    }
  },

  UPDATE_ASSETS(state, [conversation, assets]) {
    if (state.selected.id === conversation) {
      state.selected.assets = assets;
    }
  },

  UPDATE_DOCUMENTS(state, [conversation, documents]) {
    if (state.selected.id === conversation) {
      state.selected.documents = documents;
    }
  },

  REMOVE_PRIMARY_AGENT(state, [conversationId, customer]) {
    const { primaryAgent, primary_agent, ..._customer } = customer;
    if (state.selected.id === conversationId) {
      state.selected = { ...state.selected, customer: _customer };
      state.selectedCustomer = { ..._customer };
    }

    const conversation = state.conversationsByConversationId[conversationId];
    if (conversation) {
      const DEFAULT_PRIMARY_AGENT_OBJ = {
        canLock: true,
        firstName: '',
        maxLocks: 3,
      };

      conversation.primaryAgent = DEFAULT_PRIMARY_AGENT_OBJ;
      conversation.customer = customer;
    }
  },

  UPDATE_CUSTOMER(state, [customer, conversation]) {
    const customerId = get(customer, 'id');
    if (get(state, 'selected.customer_id') === customerId) {
      state.selected = { ...state.selected, customer };
      state.selectedCustomer = { ...customer };
    }

    if (conversation) {
      conversation.customer = customer;
      state.conversationsByConversationId[conversation.id] = conversation;
    }
  },

  UPDATE_CUSTOMER_PRIMARY_AGENT(state, [customer, agent]) {
    if (get(state, 'selected.customer_id', -1) === get(customer, 'id')) {
      state.selected.primaryAgent = agent;
    }
  },

  CLOSE_CONVERSATION(state, conversation) {
    const id = get(conversation, 'id', -1);
    if (state.selected.id === id) {
      state.selected = {};
    }
  },

  APPEND_CONVERSATION(state, [conversationToAdd, conversationToRemove]) {
    if (conversationToRemove && has(state.conversationsByConversationId, conversationToRemove.id)) {
      delete state.conversationsByConversationId[conversationToRemove.id];
    }

    if (conversationToAdd) {
      state.conversationsByConversationId[conversationToAdd.id] = conversationToAdd;
    }
  },

  UPDATE_CONVERSATION(state, [conversation, profile]) { // eslint-disable-line
    if (has(state.conversationsByConversationId, conversation.id)) {
      state.conversationsByConversationId[conversation.id] = conversation;
    }
  },

  // FIXME: Remove profile param
  UPDATE_LOCK_STATUS(state, [conversation, profile]) { // eslint-disable-line
    const id = get(conversation, 'id', -1);
    if (state.selected.id === id) {
      state.selected = { ...state.selected, locked_by: get(conversation, 'locked_by', null) };
    }

    if (has(state.conversationsByConversationId, conversation.id)) {
      state.conversationsByConversationId[conversation.id] = merge(omit(state.conversationsByConversationId[conversation.id], ['locked_by', 'isLocked']), conversation);
    }
  },

  UPDATE_SUGGESTIONS_LIST(state, [id, suggestions]) {
    if (state.selected.id === id) {
      updateSuggestionEntities({
        conversation: state.selected,
        customer: get(state, 'selectedCustomer', {}),
        agents: get(state, 'selected.agents', []),
        suggestions,
      });
      Events.emit('open.suggestions');
    }
  },

  SET_OPENED_SIDEBAR_TAB(state, tab) {
    state.openedSidebarTab = tab;
  },

  SET_QUICK_RESPONSES_LIST(state, { list, isNew }) {
    state.quickResponses = isNew ? [...list] : uniqBy(state.quickResponses.concat(list), 'id');
  },

  UPDATE_NEW_CONVERSATION_INDICATOR(state, value) {
    state.newConversationIndicator = value;
  },

  IS_COVERSATION_OPEN(state, value) {
    state.isConversationOpen = value;
  },

  IS_VIDEO_CONTEXT(state, value) {
    state.isVideoContext = value;
  },

  IS_COBROWSE_CONTEXT(state, value) {
    state.isCobrowseContext = value;
  },

  IS_VOICE_CONTEXT(state, value) {
    state.isVoiceCallContext = value;
  },

  MARK_AS_READ(state, [conversation]) {
    if (has(state.conversationsByConversationId, `${conversation.id}`)) {
      state.conversationsByConversationId[conversation.id].unread_message_count = 0;
      state.conversationsByConversationId[conversation.id].effective_status = 'inactive';
    }
  },

  UPDATE_POLL_LIMIT(state, value) {
    state.limit = value;
  },

  UPDATE_CUSTOMER_READ(state) {
    if (state.customerReadMsg) {
      delete state.customerReadMsg.status;
      state.customerReadMsg['status'] = null;
      state.customerReadMsg = null;
    }

    // reverse looping
    const i = findLastIndex(get(state, 'messages', []), (m) => {
      if (get(m, 'sender_type') === 'agents') {
        return true;
      }
      return false;
    });

    if (i !== -1) {
      state.customerReadMsg = state.messages[i];
      state.customerReadMsg['status'] = { state: 'viewed', message: 'Read' };
    }
  },

  SET_CHANNELS(state, value) {
    state.channels = value;
  },

  TOGGLE_IS_SUGGESTIONS_OPENED(state) {
    state.isSuggestionsOpened = !state.isSuggestionsOpened;
    localStorage.setItem('isSuggestionsOpened', state.isSuggestionsOpened);
  },

  SET_IS_SUGGESTIONS_OPENED(state, value) {
    state.isSuggestionsOpened = value;
    localStorage.setItem('isSuggestionsOpened', value);
  },

  SET_CUTOMER_FROM_ROUTE(state, value) {
    state.selectedCustomer = value;
  },

  SET_IS_TIMESTAMP_ABSOLUTE(state, value) {
    state.isTimestampAbsolute = value;
    localStorage.setItem('isTimestampAbsolute', state.isTimestampAbsolute);
  },

  SET_LAST_FEEDBACK_INFO(state, value) {
    state.selectedLastFeedbackInfo = value;
  },

  SET_MESSAGES_PAGE_INFO(state, value) {
    state.messagesPageInfo = value;
  },
};

function prepareForState({ conversations, messages, agentsById, customer }) {
  const SENDER_TYPES = {
    AGENT: 'agents',
    CUSTOMER: 'customers',
  };
  conversations.reverse();
  for (const conversation of conversations) {
    const suggestions = get(conversation, 'suggestions', []);
    updateSuggestionEntities({
      conversation,
      customer,
      agents: conversation.agents,
      suggestions,
    });
  }
  messages.reverse();
  for (const message of messages) {
    const { sender_type } = message;
    if (sender_type === SENDER_TYPES.AGENT) {
      message.sender = agentsById[message.sender_id];
    } else if (sender_type === SENDER_TYPES.CUSTOMER) {
      message.sender = customer;
    }
  }
}
